import KatalLogger, {
  Level,
  LoggerConfig,
} from '@amzn/katal-logger';
import {
  approvedUiEventName,
  UiErrorEventName,
  UiEventContext,
  UiEventName,
  UiKeyEventName,
  uiEventNameToSampleRate,
  uiEventNameToSeverity,
  valuesToSanitizedError,
  valuesToUiEventContext,
  valueToSanitizedComponentStack,
} from './dataTransformations';
import {
  authenticatedLogTransportFor,
  MAX_LOG_BATCH_BYTES,
} from './transport';

const MAX_LOG_LINE_BYTES = 8 * 1024;
const NORMAL_BATCH_SIZE = 10;
const NORMAL_BATCH_TIMEOUT_MS = 5000;
const LOGGER_URL_PATTERN =
  /^https:\/\/[^/?#\s]+(?:\/[^?#\s]*)*\/v1\/log$/;

interface KatalLoggerClient {
  info(message: string, context?: Record<string, unknown>): Promise<void>;
  error(
    message: string,
    error: Error,
    context?: Record<string, unknown>,
  ): Promise<void>;
}

export interface NavigatorUiLoggerDependencies {
  readonly isProduction: boolean;
  readonly logger: (config: LoggerConfig) => KatalLoggerClient;
  readonly request: (
    input: RequestInfo,
    init?: RequestInit,
  ) => Promise<Response>;
  readonly online: () => boolean;
  readonly wait: (durationMs: number) => Promise<void>;
  readonly random: () => number;
  readonly now: () => string;
  readonly console: Pick<Console, 'info' | 'error' | 'warn'>;
}

export interface NavigatorUiLoggerConfig {
  readonly loggerUrl: string;
  readonly stage: string;
  readonly region: string;
  readonly token: () => Promise<string | null>;
}

export interface UiErrorDetails {
  readonly componentStack?: string | null;
}

export interface NavigatorUiLogger {
  readonly event: (eventName: UiKeyEventName) => void;
  readonly error: (
    eventName: UiErrorEventName,
    error: unknown,
    details?: UiErrorDetails,
  ) => void;
}

const defaultDependencies = (): NavigatorUiLoggerDependencies => ({
  isProduction: process.env.NODE_ENV === 'production',
  logger: (config) => new KatalLogger(config),
  request: (input, init) => fetch(input, init),
  online: () => typeof navigator === 'undefined' || navigator.onLine,
  wait: (durationMs) =>
    new Promise((resolve) => window.setTimeout(resolve, durationMs)),
  random: Math.random,
  now: () => new Date().toISOString(),
  console,
});

const valueToLoggerUrl = (value: string): string | null => {
  return (
    value
    && !value.includes('{{')
    && LOGGER_URL_PATTERN.test(value)
  )
    ? value
    : null;
};

const runLoggingOperation =
  (logConsole: Pick<Console, 'warn'>) =>
  async (operation: () => Promise<void>): Promise<void> => {
    try {
      await operation();
    } catch {
      logConsole.warn('[NavigatorUiLogger] Logging operation failed.');
    }
  };

const disabledNavigatorUiLogger: NavigatorUiLogger = {
  event: () => undefined,
  error: () => undefined,
};

export const navigatorUiLoggerFor =
  (
    dependencies: NavigatorUiLoggerDependencies = defaultDependencies(),
  ) =>
  (config: NavigatorUiLoggerConfig): NavigatorUiLogger => {
    const runOperation = runLoggingOperation(dependencies.console);
    const contextFor = (eventName: UiEventName): UiEventContext =>
      valuesToUiEventContext(config.stage)(
        config.region,
      )(dependencies.now())(eventName);

    if (!dependencies.isProduction) {
      return {
        event: (eventName) => {
          dependencies.console.info(
            '[NavigatorUiEvent]',
            contextFor(eventName),
          );
        },
        error: (eventName, error, details = {}) => {
          dependencies.console.error(
            '[NavigatorUiError]',
            {
              ...contextFor(eventName),
              componentStack: valueToSanitizedComponentStack(
                details.componentStack,
              ),
            },
            valuesToSanitizedError(eventName, error),
          );
        },
      };
    }

    const loggerUrl = valueToLoggerUrl(config.loggerUrl);
    if (!loggerUrl) {
      dependencies.console.warn(
        '[NavigatorUiLogger] Logging disabled: logger URL is unavailable.',
      );
      return disabledNavigatorUiLogger;
    }

    const transport = authenticatedLogTransportFor({
      loggerUrl,
      token: config.token,
      request: dependencies.request,
      online: dependencies.online,
      wait: dependencies.wait,
    });

    const sharedConfig: LoggerConfig = {
      url: loggerUrl,
      logThreshold: Level.INFO,
      maxLogLineSize: MAX_LOG_LINE_BYTES,
      maxLogBatchSize: MAX_LOG_BATCH_BYTES,
      recordMetrics: false,
      decodeStackTrace: false,
      logToConsole: false,
      sendToBackendOverride: (batch) => {
        void runOperation(() => transport.send(batch));
      },
    };

    let normalLogger: KatalLoggerClient;
    let errorLogger: KatalLoggerClient;
    try {
      normalLogger = dependencies.logger({
        ...sharedConfig,
        maxLogsPerBatch: NORMAL_BATCH_SIZE,
        batchTimeoutDuration: NORMAL_BATCH_TIMEOUT_MS,
      });
      errorLogger = dependencies.logger({
        ...sharedConfig,
        maxLogsPerBatch: 1,
        batchTimeoutDuration: 0,
      });
    } catch {
      dependencies.console.warn(
        '[NavigatorUiLogger] Logging disabled: initialization failed.',
      );
      return disabledNavigatorUiLogger;
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        void runOperation(() => transport.flushOfflineQueue());
      });
    }

    return {
      event: (eventName) => {
        if (!approvedUiEventName(eventName)) {
          return;
        }

        if (
          dependencies.random() >= uiEventNameToSampleRate(eventName)
        ) {
          return;
        }

        if (uiEventNameToSeverity(eventName) === 'info') {
          const context = contextFor(eventName);
          void runOperation(() =>
            normalLogger.info(
              eventName,
              context as unknown as Record<string, unknown>,
            ),
          );
        }
      },
      error: (eventName, error, details = {}) => {
        if (
          dependencies.random() >= uiEventNameToSampleRate(eventName)
        ) {
          return;
        }

        const sanitizedError = valuesToSanitizedError(eventName, error);
        const context = {
          ...contextFor(eventName),
          componentStack: valueToSanitizedComponentStack(
            details.componentStack,
          ),
        };
        void runOperation(() =>
          errorLogger.error(
            eventName,
            sanitizedError,
            context as unknown as Record<string, unknown>,
          ),
        );
      },
    };
  };
